import { type ChangeEvent, useCallback, useMemo, useState } from 'react';
import { Checkbox } from '@src/components/base/Checkbox';
import { ExternalLink } from '@components/ExternalLink.tsx';
import { Heading5 } from '@components/typography/Heading5.tsx';
import { InlineComboboxEdit } from '@components/InlineComboboxEdit.tsx';
import { InlineTextEdit } from '@components/InlineTextEdit.tsx';
import { UserRole } from '@funnellab/shared/src/dtos/UserDTO';
import { getEuroCurrency } from '@utils/currency.ts';
import { isEmail } from '@src/hooks/validation/rules/regex';
import { required } from '@src/hooks/validation/rules/required';
import { usePromise } from '@hooks/usePromise.ts';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '@src/contexts/useUserContext';
import { useValidation } from '@src/hooks/validation/useValidation';
import countryService, {
  type Country,
} from '@src/api/services/CountryService.ts';
import type { OrganizationDto } from '@src/api/api.types.ts';
import { validVatNo } from '@src/hooks/validation/rules/validVatNo';
import { Divider } from '@src/components/base/Divider';

export default function Details({
  organization,
  setValue,
}: {
  organization: OrganizationDto;
  setValue: <Field extends keyof OrganizationDto>(
    fieldIdentifier: Field,
    newValue: OrganizationDto[Field] | null | undefined,
  ) => Promise<void>;
}) {
  const { user: currentUser } = useUserContext();
  const { data: countries } = usePromise(
    async () => countryService.getCountries(),
    { setLoadingStateOnRefresh: true },
    [],
  );

  const enhanceUrl = (url: string | undefined | null) => {
    if (!url) {
      return undefined;
    }
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return 'http://' + url;
  };

  const [billingEmail, setBillingEmail] = useState(organization.billingEmail);
  const [vatNo, setVatNo] = useState(organization.vatNo);

  const billingEmailValidation = useValidation(
    billingEmail,
    [required, isEmail],
    undefined,
  );

  const setPaymentByInvoice = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      void setValue('paymentByInvoice', checked);
    },
    [setValue],
  );

  const userIsAgencyOrAdmin = useMemo(() => {
    const agencyAndAdmin = [UserRole.ADMINISTRATOR, UserRole.AGENCY];
    return agencyAndAdmin.includes(currentUser.role);
  }, [currentUser.role]);

  const { t } = useTranslation('organizationDetails');

  const vatNoValidationRules = useMemo(
    () =>
      organization.country?.subdivision_code
        ? [validVatNo(organization.country.subdivision_code)]
        : [],
    [organization.country?.subdivision_code],
  );

  const vatNoValidation = useValidation(vatNo, vatNoValidationRules, undefined);
  return (
    <div className="flex flex-col md:flex-row gap-4 md:gap-6">
      <Heading5 className="md:hidden">{t('details')}</Heading5>
      <div className="md:w-1/2 md:border border-blue-100 rounded-2xl">
        <div className="h-fit basis-1/2 flex flex-col md:grid grid-cols-2 md:p-4 md:gap-4">
          <Heading5>{t('homepage')}</Heading5>
          <InlineTextEdit
            value={organization.homepage ?? ''}
            onSubmit={(newHomepage) =>
              setValue('homepage', enhanceUrl(newHomepage))
            }
            emptyDisplayValue="-"
            ValueComponent={(props) => (
              <ExternalLink
                {...props}
                className={organization.homepage ? 'underline' : ''}
                text={organization.homepage ?? '-'}
                href={enhanceUrl(organization.homepage)}
              />
            )}
            className="bg-gray-100 md:bg-white"
          />
          <Heading5>{t('branch')}</Heading5>
          <InlineTextEdit
            value={organization.branch ?? ''}
            onSubmit={(newBranch) => setValue('branch', newBranch)}
            emptyDisplayValue="-"
            className="bg-gray-100 md:bg-white"
          />
          <Heading5>{t('numberOfEmployees')}</Heading5>
          <InlineTextEdit
            value={organization.numberOfEmployees ?? ''}
            onSubmit={(newNumberOfEmployees) =>
              setValue('numberOfEmployees', newNumberOfEmployees)
            }
            emptyDisplayValue="-"
            className="bg-gray-100 md:bg-white"
          />
          {organization.credit && (
            <>
              <Heading5>{t('credit')}</Heading5>
              <div>{getEuroCurrency(organization.credit)}</div>
            </>
          )}
          {userIsAgencyOrAdmin && (
            <>
              <Heading5>{t('paymentByInvoice')}</Heading5>
              <div className="flex justify-end md:justify-start">
                <Checkbox
                  checked={organization.paymentByInvoice}
                  onChange={setPaymentByInvoice}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <Divider />
      <Heading5 className="md:hidden">{t('billingSection')}</Heading5>
      <div className="md:border border-blue-100 rounded-xl basis-1/2 flex flex-col md:grid grid-cols-2 md:p-4 md:gap-4 content-start">
        <Heading5>{t('street')}</Heading5>
        <InlineTextEdit
          value={organization.street ?? ''}
          onSubmit={(newStreet) => setValue('street', newStreet)}
          emptyDisplayValue="-"
          className="bg-gray-100 md:bg-white"
        />
        <Heading5>{t('zipCode')}</Heading5>
        <InlineTextEdit
          value={organization.zip ?? ''}
          onSubmit={(newZipCode) => setValue('zip', newZipCode)}
          emptyDisplayValue="-"
          className="bg-gray-100 md:bg-white"
        />
        <Heading5>{t('city')}</Heading5>
        <InlineTextEdit
          value={organization.city ?? ''}
          onSubmit={(newCity) => setValue('city', newCity)}
          emptyDisplayValue="-"
          className="bg-gray-100 md:bg-white"
        />
        <Heading5>{t('country')}</Heading5>
        <InlineComboboxEdit<Country | null>
          displayValue={(value) => value?.label ?? ''}
          value={organization.country as Country}
          onSubmit={(newCountry) =>
            setValue('country', newCountry as OrganizationDto['country'])
          }
          emptyDisplayValue="-"
          options={countries ?? []}
          className="bg-gray-100 md:bg-white"
        />
        <Heading5>{t('vatNo')}</Heading5>
        <InlineTextEdit
          value={organization.vatNo ?? ''}
          onSubmit={(newVatNo) => setValue('vatNo', newVatNo)}
          onChange={setVatNo}
          emptyDisplayValue="-"
          validation={vatNoValidation}
          showError={true}
          disabled={!organization.country}
          passthrough={{
            inlineEdit: {
              submitButton: { disabled: !vatNoValidation.isValid },
            },
          }}
          className="bg-gray-100 md:bg-white"
        />
        <Heading5>{t('billingEmail')}</Heading5>
        <InlineTextEdit
          value={organization.billingEmail ?? ''}
          onSubmit={(newBillingEmail) =>
            setValue('billingEmail', newBillingEmail)
          }
          onChange={setBillingEmail}
          emptyDisplayValue="-"
          validation={billingEmailValidation}
          showError={true}
          passthrough={{
            inlineEdit: {
              submitButton: { disabled: !billingEmailValidation.isValid },
            },
          }}
          className="bg-gray-100 md:bg-white"
        />
      </div>
    </div>
  );
}
